/**
 * First-launch routing, port of AppRootView: on a cold start at `/`,
 *  - a brand-new player gets the first-launch cold open (incoming call, lock screen), then the client chat;
 *  - a player who has not done anything in the case yet goes straight to the client chat;
 *  - everyone else lands on the Home.
 * Decided once per page load, so going back to the Home later never redirects.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { isUntouchedProgress, type EpisodeProgress } from '@/domain';
import { preloadColdOpen } from '@/features/coldopen/assets';
import { track } from '@/services/analytics';
import { useSession } from './providers';
import { ClientChatView, ColdOpenScreen, CopiedPhoneView } from './routeComponents';

export const COLD_OPEN_COMPLETED_KEY = 'detectea.firstLaunchColdOpenCompleted';

interface ColdOpen {
  active: boolean;
  /** From `finish()` until the router shows `/play`: the screen must already render the chat, never the Home. */
  handingOff: boolean;
  finish(): void;
}
const ColdOpenContext = createContext<ColdOpen>({ active: false, handingOff: false, finish: () => undefined });
export const useColdOpen = () => useContext(ColdOpenContext);

function coldOpenCompleted(): boolean {
  try { return localStorage.getItem(COLD_OPEN_COMPLETED_KEY) === '1'; } catch { return false; }
}

/** AppRootView.shouldShowFirstLaunchColdOpen. */
export function shouldShowFirstLaunchColdOpen(progress: EpisodeProgress, completed: boolean): boolean {
  const hasStartedOpening = (progress.playerFirstName?.trim() ?? '') !== ''
    || (progress.onboardingReplyText?.trim() ?? '') !== ''
    || progress.copiedPhoneUnlockedAtLeastOnce === true;
  return !completed && !hasStartedOpening && isUntouchedProgress(progress);
}

type Decision = 'coldOpen' | 'chat' | 'stay';

/** The boot redirect to the chat happens on the first commit only, never on a later visit to the Home. */
let bootRedirectConsumed = false;

export function ColdOpenProvider({ children }: { children: ReactNode }) {
  const { progress, episode } = useSession();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [decision] = useState<Decision>(() => pathname !== '/' || !progress || !episode ? 'stay'
    : shouldShowFirstLaunchColdOpen(progress, coldOpenCompleted()) ? 'coldOpen'
      : isUntouchedProgress(progress) ? 'chat' : 'stay');
  const [active, setActive] = useState(decision === 'coldOpen');
  const [handingOff, setHandingOff] = useState(false);
  // Navigation and state updates may commit separately: the hand-off ends with the first location change.
  const [handoffPath, setHandoffPath] = useState(pathname);
  if (pathname !== handoffPath) {
    setHandoffPath(pathname);
    if (handingOff) setHandingOff(false);
  }
  useEffect(() => { bootRedirectConsumed = true; }, []);
  const episodeID = episode?.id;

  useEffect(() => {
    if (decision !== 'coldOpen') return;
    // Wallpapers and portraits decoded while the splash still covers, so the first frame paints complete.
    if (episodeID) void preloadColdOpen(episodeID);
    // The screens the cold open hands off to are ready (chunks loaded) before it ends.
    const swallow = () => undefined;
    void ColdOpenScreen.preload().catch(swallow);
    void ClientChatView.preload().catch(swallow);
    void CopiedPhoneView.preload().catch(swallow);
    track('first_launch_cold_open_started', { episode_id: episodeID, source: 'first_launch' });
  }, [decision, episodeID]);

  const finish = useCallback(() => {
    try { localStorage.setItem(COLD_OPEN_COMPLETED_KEY, '1'); } catch { /* private mode: replays next launch */ }
    track('first_launch_cold_open_completed', { episode_id: episodeID });
    setHandingOff(true);
    setActive(false);
    void navigate('/play');
  }, [episodeID, navigate]);

  const value = useMemo(() => ({ active, handingOff, finish }), [active, handingOff, finish]);
  const redirect = decision === 'chat' && !bootRedirectConsumed && pathname === '/';
  return (
    <ColdOpenContext.Provider value={value}>
      {redirect ? <Navigate to="/play" replace /> : children}
    </ColdOpenContext.Provider>
  );
}
